import React, { useEffect, useMemo } from 'react';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Alert, Form, Input, Row, Col, Select } from 'antd';
import { FileOutlined, SendOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ModalChromeHeading,
    PanelSectionLabel,
} from '@/pages/inside/tenant/style';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionFormItemStyle,
    submissionRichDescriptionLabelStyle,
} from '@/pages/inside/tenant/project/projectTabStyles';
import { LINK_URL_EXAMPLE_HELP, normalizeUrlOnFirstType, validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';

const LinkModalFormWrap = styled.div`
    .ant-form-item-explain,
    .ant-form-item-explain-error {
        font-size: 12px;
        line-height: 1.25;
        margin-top: 2px;
    }
`;

const linkTitle = <ModalChromeHeading>Add link</ModalChromeHeading>;

/**
 * Create sprint task from an issue/upgrade workflow (Issues & Upgrades Kanban card menu).
 * Form + multipart behavior aligns with sprint task create in {@link SprintsTabModals}.
 */
export default function IssueUpgradeCreateTaskModal({
    modalOpen,
    onModalCancel,
    onModalSubmit,
    submitPending,
    taskForm,
    taskPendingAttachments,
    handleTaskFilesSelected,
    removeTaskAttachment,
    pendingLinkAttachments,
    removePendingLinkAttachment,
    handleEditorLinkRemoved,
    handleLinkRequest,
    linkModalOpen,
    setLinkModalOpen,
    linkModalForm,
    linkModalCallbackRef,
    handleLinkModalSubmit,
    mentionUsers,
    mentionMilestones,
    mentionSprints,
    milestoneList = [],
    sprintList = [],
    truncateFileName,
    getMilestoneDisplayLabel,
    getSprintDisplayLabel,
    canCreateTaskFromIssue,
    canViewSprintsForTaskCreate,
    useMilestonesEnabled,
    usePhasesEnabled,
    useSprintsEnabled,
    phaseList = [],
}) {
    const milestoneWatch = Form.useWatch('milestoneId', taskForm);
    const phaseWatch = Form.useWatch('phaseId', taskForm);
    const sprintWatch = Form.useWatch('sprintId', taskForm);

    useEffect(() => {
        if (!modalOpen) return;
        const id = requestAnimationFrame(() => {
            document
                .querySelectorAll('.ant-modal-wrap.modal-size-md.ant-modal-wrap-open .ant-modal-body')
                .forEach((el) => {
                    el.scrollTop = 0;
                });
        });
        return () => cancelAnimationFrame(id);
    }, [modalOpen]);

    useEffect(() => {
        if (!modalOpen) return;
        let scoped = sprintList;
        if (useMilestonesEnabled) {
            const mid = milestoneWatch != null && milestoneWatch !== '' ? String(milestoneWatch) : null;
            scoped = mid != null
                ? sprintList.filter((sp) => String(sp?.milestoneId ?? sp?.milestone?.id ?? '') === mid)
                : sprintList;
        } else if (usePhasesEnabled) {
            const pid = phaseWatch != null && phaseWatch !== '' ? String(phaseWatch) : null;
            scoped = pid != null
                ? sprintList.filter((sp) => String(sp?.phaseId ?? sp?.phase?.id ?? '') === pid)
                : sprintList;
        }
        const allowedIds = new Set(scoped.map((s) => String(s?.id ?? '')));
        if (sprintWatch != null && sprintWatch !== '' && !allowedIds.has(String(sprintWatch))) {
            taskForm.setFieldValue('sprintId', undefined);
        }
    }, [modalOpen, milestoneWatch, phaseWatch, sprintList, sprintWatch, taskForm, useMilestonesEnabled, usePhasesEnabled]);

    const sprintOptions = useMemo(() => {
        let list = sprintList;
        if (useMilestonesEnabled) {
            const mid = milestoneWatch != null && milestoneWatch !== '' ? String(milestoneWatch) : null;
            list = mid != null
                ? sprintList.filter((sp) => String(sp?.milestoneId ?? sp?.milestone?.id ?? '') === mid)
                : sprintList;
        } else if (usePhasesEnabled) {
            const pid = phaseWatch != null && phaseWatch !== '' ? String(phaseWatch) : null;
            list = pid != null
                ? sprintList.filter((sp) => String(sp?.phaseId ?? sp?.phase?.id ?? '') === pid)
                : sprintList;
        }
        return list.map((s) => ({
            value: s.id,
            label: getSprintDisplayLabel ? getSprintDisplayLabel(s) : (s?.name ?? s?.title ?? String(s?.id ?? '')),
        }));
    }, [milestoneWatch, phaseWatch, sprintList, getSprintDisplayLabel, useMilestonesEnabled, usePhasesEnabled]);

    const milestoneOptions = useMemo(
        () =>
            milestoneList.map((m) => ({
                value: m.id,
                label: getMilestoneDisplayLabel ? getMilestoneDisplayLabel(m) : (m?.name ?? String(m?.id ?? '')),
            })),
        [milestoneList, getMilestoneDisplayLabel],
    );

    const phaseOptions = useMemo(
        () =>
            phaseList.map((p) => ({
                value: p.id,
                label: p?.name ?? p?.title ?? String(p?.id ?? ''),
            })),
        [phaseList],
    );

    const blockedBySprintsPermission = Boolean(canCreateTaskFromIssue && !canViewSprintsForTaskCreate);

    return (
        <>
            <AppModal
                size="md"
                title={<ModalChromeHeading>Create task from issue</ModalChromeHeading>}
                open={modalOpen}
                onCancel={onModalCancel}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button $primary={false} $size="14px" onClick={onModalCancel}>
                            Cancel
                        </Button>
                        <Button
                            $primary
                            $blackOnLight
                            $size="14px"
                            loading={submitPending}
                            icon={<SendOutlined />}
                            disabled={!canCreateTaskFromIssue || (useSprintsEnabled && blockedBySprintsPermission)}
                            onClick={onModalSubmit}
                        >
                            Create task
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    {useSprintsEnabled && blockedBySprintsPermission ? (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Sprint selection unavailable"
                            description="You can create tasks here, but you do not have permission to view project sprints, so sprint options cannot load."
                        />
                    ) : null}
                    <Form form={taskForm} layout="vertical" requiredMark={false} styles={projectSprintTaskModalFormStyles}>
                        <Row gutter={[16, 8]}>
                            {useMilestonesEnabled && (
                                <Col xs={24} md={useSprintsEnabled ? 12 : 24}>
                                    <Form.Item
                                        name="milestoneId"
                                        label={<span style={submissionRichDescriptionLabelStyle}>Milestone</span>}
                                    >
                                        <Select
                                            size="large"
                                            allowClear
                                            placeholder={
                                                milestoneOptions.length === 0 ? 'No milestones' : 'Filter by milestone'
                                            }
                                            options={milestoneOptions}
                                            disabled={milestoneOptions.length === 0}
                                            onChange={() => taskForm.setFieldValue('sprintId', undefined)}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            {!useMilestonesEnabled && usePhasesEnabled && (
                                <Col xs={24} md={useSprintsEnabled ? 12 : 24}>
                                    <Form.Item
                                        name="phaseId"
                                        label={<span style={submissionRichDescriptionLabelStyle}>Phase</span>}
                                    >
                                        <Select
                                            size="large"
                                            allowClear
                                            placeholder={
                                                phaseOptions.length === 0 ? 'No phases' : 'Filter by phase'
                                            }
                                            options={phaseOptions}
                                            disabled={phaseOptions.length === 0}
                                            onChange={() => taskForm.setFieldValue('sprintId', undefined)}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            {useSprintsEnabled && (
                                <Col xs={24} md={useMilestonesEnabled || usePhasesEnabled ? 12 : 24}>
                                    <Form.Item
                                        name="sprintId"
                                        label={<span style={submissionRichDescriptionLabelStyle}>Sprint</span>}
                                        rules={[{ required: true, message: 'Select a sprint' }]}
                                    >
                                        <Select
                                            size="large"
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder={sprintOptions.length === 0 ? 'No sprints' : 'Select sprint'}
                                            options={sprintOptions}
                                            disabled={sprintOptions.length === 0 || blockedBySprintsPermission}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                        <Row gutter={[16, 8]}>
                            <Col xs={24} md={14}>
                                <Form.Item
                                    name="title"
                                    label={<span style={submissionRichDescriptionLabelStyle}>Title</span>}
                                    rules={[{ required: true, message: 'Enter task title' }]}
                                >
                                    <Input placeholder="e.g. Fix issue raised in IAU" size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={5}>
                                <Form.Item
                                    name="priority"
                                    label={<span style={submissionRichDescriptionLabelStyle}>Priority</span>}
                                    initialValue="medium"
                                >
                                    <Select
                                        size="large"
                                        options={[
                                            { value: 'high', label: 'High' },
                                            { value: 'medium', label: 'Medium' },
                                            { value: 'low', label: 'Low' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={5}>
                                <Form.Item
                                    name="type"
                                    label={<span style={submissionRichDescriptionLabelStyle}>Task Type</span>}
                                    initialValue="feature"
                                    rules={[{ required: true, message: 'Select task type' }]}
                                >
                                    <Select
                                        size="large"
                                        options={[
                                            { value: 'feature', label: 'Feature' },
                                            { value: 'improvement', label: 'Improvement' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            name="description"
                            style={submissionRichDescriptionFormItemStyle}
                            label={<span style={submissionRichDescriptionLabelStyle}>Description</span>}
                        >
                            <RichTextEditor
                                submissionNeutralLight
                                isAttachment
                                isLink
                                isMention
                                placeholder="Add description — bold, lists, @mentions. Attach files or links."
                                showAttachButton
                                mentionUsers={mentionUsers}
                                mentionMilestones={mentionMilestones}
                                mentionSprints={mentionSprints}
                                onFilesSelected={handleTaskFilesSelected}
                                onLinkRequest={handleLinkRequest}
                                onLinkRemoved={handleEditorLinkRemoved}
                                attachmentIndexOffset={taskPendingAttachments.length}
                                onAttachmentRemoved={(index) => {
                                    const p = taskPendingAttachments[index];
                                    if (p) removeTaskAttachment(p.id);
                                }}
                            />
                        </Form.Item>
                        {taskPendingAttachments.length > 0 && (
                            <div
                                style={{
                                    marginTop: 8,
                                    marginBottom: 16,
                                    padding: '8px 0',
                                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                                }}
                            >
                                <PanelSectionLabel
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        marginBottom: 8,
                                        fontSize: 11,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        opacity: 0.75,
                                    }}
                                >
                                    <FileOutlined />
                                    Attachments
                                </PanelSectionLabel>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                        gap: 10,
                                    }}
                                >
                                    {taskPendingAttachments.map((p) => (
                                        <PendingAttachmentTile
                                            key={p.id}
                                            item={p}
                                            onRemove={() => removeTaskAttachment(p.id)}
                                            truncateFileName={truncateFileName}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        {pendingLinkAttachments.length > 0 && modalOpen ? (
                            <PendingLinkWrap links={pendingLinkAttachments} onRemoveLink={removePendingLinkAttachment} />
                        ) : null}
                    </Form>
                </CreateRoleModalBody>
            </AppModal>

            <AppModal
                size="sm"
                title={linkTitle}
                open={linkModalOpen}
                onCancel={() => {
                    setLinkModalOpen(false);
                    linkModalCallbackRef.current = null;
                }}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button
                            $primary={false}
                            $size="14px"
                            onClick={() => {
                                setLinkModalOpen(false);
                                linkModalCallbackRef.current = null;
                            }}
                        >
                            Cancel
                        </Button>
                        <Button $primary $blackOnLight $size="14px" icon={<SendOutlined />} onClick={handleLinkModalSubmit}>
                            Add link
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    <LinkModalFormWrap>
                        <Form form={linkModalForm} layout="vertical" requiredMark={false}>
                            <Form.Item noStyle shouldUpdate={(prev, next) => prev?.url !== next?.url}>
                                {() => {
                                    const urlErrors = linkModalForm?.getFieldError?.('url') || [];
                                    const showExample = urlErrors.length === 0;
                                    return (
                                        <Form.Item
                                            name="url"
                                            label={<span style={{ fontWeight: 600, fontSize: 13 }}>URL</span>}
                                            rules={[
                                                {
                                                    validator: async (_rule, value) => {
                                                        const check = validateLinkUrlInput(value);
                                                        if (check.ok) return Promise.resolve();
                                                        return Promise.reject(new Error(check.message || 'Enter a valid URL'));
                                                    },
                                                },
                                            ]}
                                            extra={showExample ? LINK_URL_EXAMPLE_HELP : null}
                                        >
                                            <Input
                                                placeholder="https://..."
                                                size="large"
                                                onChange={(e) => {
                                                    const raw = e?.target?.value ?? '';
                                                    const prev = linkModalForm?.getFieldValue?.('url');
                                                    const normalized = normalizeUrlOnFirstType(raw, prev);
                                                    if (normalized !== raw) {
                                                        linkModalForm?.setFieldsValue?.({ url: normalized });
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                            <Form.Item name="title" label={<span style={{ fontWeight: 600, fontSize: 13 }}>Link text</span>}>
                                <Input placeholder="Optional display text" size="large" />
                            </Form.Item>
                        </Form>
                    </LinkModalFormWrap>
                </CreateRoleModalBody>
            </AppModal>
        </>
    );
}
